import { routes } from '@sketch/modules-common'
import { useVersioning } from '../../../../versioning'

/** The Components Web View page a linked component lives on. */
export type ComponentDocumentRouteKey =
  | 'CWV_COLOR_VARIABLES'
  | 'CWV_TEXT_STYLES'
  | 'CWV_LAYER_STYLES'

type Args = {
  routeKey: ComponentDocumentRouteKey
  /** True when the component comes from another document (a library). */
  isForeign: boolean
  /** The share the component belongs to, when it is foreign. */
  shareID?: string
  search?: string
  /**
   * The share being viewed. Defaults to the one from versioning context;
   * callers that already hold it can pass it to avoid any disagreement.
   */
  shareIdentifier?: string
}

/**
 * Where a link to an inspected component's document should point.
 *
 * A foreign component always links to the latest version of its own document;
 * a component from the document being viewed gets a versioned link. That rule
 * was written out separately for colour variables and for text/layer styles,
 * which is what this shares — the two rows look different, so only the
 * decision lives here, not the presentation.
 */
export function useComponentDocumentLink({
  routeKey,
  isForeign,
  shareID,
  search,
  shareIdentifier,
}: Args) {
  const { share } = useVersioning()

  if (isForeign) {
    return {
      isVersioned: false as const,
      to: { pathname: routes[routeKey].create({ shareID: shareID! }), search },
    }
  }

  return {
    isVersioned: true as const,
    to: {
      routeKey,
      routeParams: { shareID: shareIdentifier ?? share.identifier },
      search,
    },
  }
}
